import { useZoomPan } from '@hakimo-ui/hakimo/ui-elements';
import { ReactNode, useEffect, useRef } from 'react';
import TileZoomControls from './TileZoomControls';

interface Props {
  children: ReactNode;
  showZoomControls?: boolean;
  trailing?: ReactNode;
  onZoomChange?: (isZoomed: boolean) => void;
}

export function TileZoomable(props: Props) {
  const { children, showZoomControls = true, trailing, onZoomChange } = props;
  const {
    zoomLevel,
    isZoomed,
    isPanning,
    isDragging,
    translate,
    onZoomIn,
    onZoomOut,
    onReset,
    onPointer,
    onPan,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  } = useZoomPan();

  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;

  // Dense grids hide the controls, so drop any zoom that is already applied —
  // otherwise the tile stays zoomed with no way to reset it.
  useEffect(() => {
    if (!showZoomControls) onReset();
  }, [showZoomControls, onReset]);

  // Report zoom state up so the grid can suspend tile dragging while zoomed —
  // otherwise panning the zoomed feed and reordering the tile fight each other.
  useEffect(() => {
    onZoomChangeRef.current?.(isZoomed);
  }, [isZoomed]);

  useEffect(() => () => onZoomChangeRef.current?.(false), []);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        className="h-full w-full"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoomLevel})`,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>

      {isZoomed && (
        <div
          className="absolute inset-0 z-[5]"
          style={{
            cursor: isPanning ? (isDragging ? 'grabbing' : 'grab') : 'default',
            pointerEvents: isPanning ? 'auto' : 'none',
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        />
      )}

      <TileZoomControls
        isZoomed={isZoomed}
        isPanning={isPanning}
        showZoomControls={showZoomControls}
        onPointer={onPointer}
        onPan={onPan}
        onReset={onReset}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        trailing={trailing}
      />
    </div>
  );
}

export default TileZoomable;
